import React, { useState } from 'react';
import { CheckCircle2, ChevronDown, Circle } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import type { ReadinessResponse, RosterMember } from '../../types/aai';
import { DimensionRanges, ErrorNote, Loading, StagePill } from './components';
import { useAsync } from './useAsync';

const MemberRow: React.FC<{ member: RosterMember; canExpand: boolean }> = ({ member, canExpand }) => {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<ReadinessResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = async () => {
    if (!canExpand || !member.onboarded) return;
    const next = !open;
    setOpen(next);
    if (next && !detail) {
      setLoading(true);
      setError(null);
      try {
        // The backend writes an audit row before returning this (R1).
        const res = await aaiApi.getIndividualReadiness(member.email);
        setDetail(res.data);
      } catch {
        setError('Could not load this member’s readiness.');
      } finally {
        setLoading(false);
      }
    }
  };

  const expandable = canExpand && member.onboarded;

  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        type="button"
        onClick={toggle}
        disabled={!expandable}
        className={`w-full flex items-center gap-3 py-3 text-left ${expandable ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'}`}
      >
        {member.onboarded
          ? <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />
          : <Circle size={16} className="text-gray-300 shrink-0" />}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-900 truncate">{member.name}</p>
          <p className="text-xs text-gray-500 truncate">{member.email}</p>
        </div>
        {member.department && <span className="text-xs text-gray-500 hidden sm:inline">{member.department}</span>}
        <span className="text-xs text-gray-400 w-28 truncate hidden md:inline">{member.workspace_role || '—'}</span>
        <span className="text-xs text-gray-500 w-20 text-right">{member.onboarded ? 'Onboarded' : 'Pending'}</span>
        {expandable && <ChevronDown size={16} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />}
      </button>

      {open && (
        <div className="pb-4 pl-7 pr-2">
          {loading && <Loading label="Loading readiness…" />}
          {error && <ErrorNote message={error} />}
          {detail && (() => {
            const summary = detail.brief?.fields?.['profile.summary']?.value;
            const title = detail.brief?.fields?.['job_title']?.value;
            const jobDesc = detail.brief?.fields?.['job_description']?.value;
            return (
              <div className="rounded-lg bg-gray-50 border border-gray-100 p-4 space-y-3">
                {/* AI-summarized profile card (F6 P0) — visible to champion/trainer. */}
                {typeof summary === 'string' && summary.trim() && (
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1">Profile{typeof title === 'string' && title ? ` · ${title}` : ''}</div>
                    <p className="text-[13px] text-gray-600 leading-relaxed">{summary}</p>
                  </div>
                )}
                {/* ROL-3: HR's job description — what the role covers. */}
                {typeof jobDesc === 'string' && jobDesc.trim() && (
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1">Role description (from HR)</div>
                    <p className="text-[13px] text-gray-600 leading-relaxed">{jobDesc}</p>
                  </div>
                )}
                {detail.readiness && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs text-gray-500">Current stage</span>
                      <StagePill stage={detail.readiness.stage} />
                    </div>
                    <DimensionRanges dimensions={detail.readiness.dimensions} />
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

const RosterTab: React.FC<{ canViewIndividual: boolean }> = ({ canViewIndividual }) => {
  const { data, loading, error } = useAsync(() => aaiApi.getMembers(), 'Could not load the roster.');

  if (loading) return <Loading label="Loading the roster…" />;
  if (error) return <ErrorNote message={error} />;
  if (!data) return null;

  const onboardedCount = data.members.filter((m) => m.onboarded).length;

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-gray-500">
          {data.members.length} member{data.members.length === 1 ? '' : 's'} · {onboardedCount} onboarded
          {data.department && <> · {data.department}</>}
        </p>
        {canViewIndividual && <span className="text-xs text-gray-400">Opening a member is logged for privacy</span>}
      </div>
      <div className="bg-white rounded-xl border border-gray-100 px-4">
        {data.members.map((m) => (
          <MemberRow key={m.email} member={m} canExpand={canViewIndividual} />
        ))}
        {data.members.length === 0 && <p className="py-6 text-sm text-gray-500 text-center">No members in scope.</p>}
      </div>
    </div>
  );
};

export default RosterTab;
